import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {client} from "../../services/api-client";
import {AxiosError} from "axios";
import {IContact, ISocialMedia} from "../../types/generalTypes";
import {toast} from "react-toastify";


export interface IUpdateAdvertiserPayload {
    payload: {
        id: number,
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface ICreateAdvertiserPayload {
    payload: {
        name: string,
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface IAdvertiser {
    id: number,
    name: string,
    socialMedia: null | ISocialMedia,
    contact: null | IContact
}

interface IGetIAdvertisersResp {
    totalItems: number,
    pageNum: number,
    pageSize: number,
    message: string,
    isOK: boolean,
    sort: null | string,
    sortField: null | string,
    search: null | string,
    items: IAdvertiser[]
}

interface IAdvertisersState {
    advertisers: IAdvertiser[],
    totalCount: number,
    pageNumber: number,
    itemsPerPage: number,
    pageSize: number
}

const initialState: IAdvertisersState = {
    advertisers: [],
    totalCount: 0,
    pageNumber: 1,
    itemsPerPage: 25,
    pageSize: 0
};


export const getAdvertisers = createAsyncThunk<IGetIAdvertisersResp, string, { rejectValue: string }>(
    'advertisers/get-advertisers',
    async (queryParams, {rejectWithValue}) => {
        try {
            const {data} = await client.get(`api/crm/advertisers${queryParams}`);
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

export const updateAdvertiser = createAsyncThunk<IAdvertiser, IUpdateAdvertiserPayload, { rejectValue: string }>(
    'advertisers/update-advertiser',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.put(`api/crm/advertisers`, body.payload);
            toast.info("SDC: Advertiser has been updated")
            body.closeForm();
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

export const createAdvertiser = createAsyncThunk<IAdvertiser, ICreateAdvertiserPayload, { rejectValue: string }>(
    'advertisers/create-advertiser',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.post(`api/crm/advertisers`, body.payload);
            toast.info("SDC: Advertiser has been created")
            body.closeForm();
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

const advertisersSlice = createSlice({
    name: 'advertisers',
    initialState,
    reducers: {
        setPageNumber: (state, action: PayloadAction<number>) => {
            state.pageNumber = action.payload;
        },
        setItemsPerPage: (state, action: PayloadAction<number>) => {
            state.itemsPerPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAdvertisers.fulfilled, (state, {payload}) => {
                state.advertisers = payload.items;
                state.pageNumber = payload.pageNum;
                state.totalCount = payload.totalItems;
                state.pageSize = payload.pageSize;
            })
            .addCase(updateAdvertiser.fulfilled, (state, action) => {
                state.advertisers = state.advertisers.map(advertiser => advertiser.id === action.payload.id ? action.payload : advertiser);
            })
            .addCase(createAdvertiser.fulfilled, (state, action) => {
                state.advertisers.unshift(action.payload);
            })
    }
});

export const {setPageNumber, setItemsPerPage} = advertisersSlice.actions;
export default advertisersSlice.reducer;
